import React, { useState } from 'react';
import { Alert, Badge, Button, Empty, PageHeader, Progress, Result, Skeleton, Statistic, Table, Tabs, Tag, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import useIndexHealth, { REFRESH_MS } from '../../components/business/pathfinder/useIndexHealth';
import DriveSearch from '../../components/business/pathfinder/DriveSearch';
import { formatBytes, formatCount, formatDuration } from '../../components/business/pathfinder/format';
import { IIndexHealth, IIndexRoot } from '../../types/models/pathfinder';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;

  .body {
    padding: 0 24px 24px;
  }

  .tab-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .counts {
    display: flex;
    flex-wrap: wrap;
    gap: 48px;
  }

  .cache {
    max-width: 320px;
  }

  .note {
    color: var(--ancana-gray);
    font-size: 12px;
    max-width: 70ch;
  }

  .error-text {
    font-family: monospace;
    font-size: 12px;
  }
`;

const HEALTH_TAB = 'health';
const SEARCH_TAB = 'search';

/** An absolute timestamp, for hovering over a relative one. */
const absolute = (iso?: string | null) =>
  iso ? dayjs(iso).format('D MMM YYYY, HH:mm:ss') : 'never';

/**
 * Why the index is not healthy, in the service's own terms.
 *
 * "Degraded" on its own is a word nobody can act on. Each reason below names
 * the mechanism and what it costs, because the reasons are not equivalent: a
 * failed crawl means the index is wrong, while a missing cursor means it is
 * merely expensive to keep right.
 */
const reasonsFor = (health: IIndexHealth): string[] => {
  const reasons: string[] = [];

  if (health.status === 'unavailable') {
    return [health.detail || 'ancana_api could not read Pathfinder at all.'];
  }

  if (!health.roots.length) {
    reasons.push('Pathfinder is tracking no Drive roots at all — nothing is being indexed.');
  }

  const failed = health.roots.filter(r => r.last_status && r.last_status !== 'ok');
  failed.forEach(root => {
    reasons.push(
      `Last crawl of "${root.root_key}" did not succeed (${root.last_status}). `
      + 'The index for that root is out of date by an unknown amount.',
    );
  });

  const cursorless = health.roots.filter(r => !r.has_page_token).map(r => r.root_key);
  if (cursorless.length) {
    reasons.push(
      `No Drive changes cursor on ${cursorless.join(' and ')}. `
      + 'Without one, a root cannot delta sync: it re-crawls in full on every pass, so its '
      + 'freshness is bounded by how long a full crawl takes rather than by the poll interval. '
      + 'Expected until the service account is granted membership of the Shared Drive.',
    );
  }

  return reasons;
};

/**
 * The one thing on this page that has to be impossible to miss.
 *
 * Degraded and unavailable are both rendered as errors, not warnings. A stale
 * index that reads as "mostly fine" is precisely the failure this monitoring
 * page exists to prevent, and a yellow banner is how that failure gets shipped.
 */
const StatusBanner = ({ health }: { health: IIndexHealth }) => {
  const reasons = reasonsFor(health);

  if (health.status === 'ok') {
    return (
      <Alert
        type="success"
        showIcon
        message="Index is fresh"
        description="Every tracked root synced on schedule and holds a Drive changes cursor."
      />
    );
  }

  const message = health.status === 'unavailable'
    ? 'Pathfinder could not be reached'
    : health.status === 'degraded'
      ? 'Index is DEGRADED — do not treat it as current'
      : 'Index status is unknown';

  return (
    <Alert
      type="error"
      showIcon
      message={message}
      description={
        reasons.length ? (
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {reasons.map(reason => <li key={reason}>{reason}</li>)}
          </ul>
        ) : 'Pathfinder reports a problem but gave no per-root detail.'
      }
    />
  );
};

/**
 * Staleness is coloured by Pathfinder's own verdict for the root, never by a
 * threshold invented here. The office does not know this deployment's poll
 * interval or how long a full crawl takes, so any "older than N minutes is bad"
 * rule it made up would eventually disagree with the service — and the office
 * would be the one that was wrong.
 */
const columns = [
  {
    title: 'Root',
    dataIndex: 'root_key',
    key: 'root_key',
    render: (key: string) => <strong>{key}</strong>,
  },
  {
    title: 'Index age',
    key: 'stale',
    render: (_: any, root: IIndexRoot) => (
      <Tooltip title={`Last crawl completed ${absolute(root.last_sync)}`}>
        <span>{root.last_sync ? `${formatDuration(root.stale_seconds)} old` : 'never synced'}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Last crawl',
    key: 'last_status',
    render: (_: any, root: IIndexRoot) => {
      if (!root.last_status) return <Badge status="default" text="no crawl yet" />;
      return root.last_status === 'ok'
        ? <Badge status="success" text="ok" />
        : <Badge status="error" text={root.last_status} />;
    },
  },
  {
    title: 'Changes cursor',
    key: 'has_page_token',
    render: (_: any, root: IIndexRoot) => (root.has_page_token
      ? <Tag color="green">Present — delta sync</Tag>
      : (
        <Tooltip title="This root re-crawls in full on every sync.">
          <Tag color="red">Missing — full re-crawl</Tag>
        </Tooltip>
      )),
  },
  {
    title: 'Last reconcile',
    key: 'last_reconcile',
    render: (_: any, root: IIndexRoot) => (root.last_reconcile
      ? <span>{absolute(root.last_reconcile)}</span>
      : <span>—</span>),
  },
  {
    title: 'Error',
    dataIndex: 'last_error',
    key: 'last_error',
    render: (error?: string | null) => (error
      // Drive's own error text: rendered as text, never as markup.
      ? <span className="error-text">{error}</span>
      : <span>—</span>),
  },
];

/**
 * Drive Index — is Pathfinder's picture of Drive current?
 *
 * Pathfinder indexes the `propiedades` and `sales` Drive folders and serves
 * that index to agents. Agents cannot tell a fresh index from a stale one, so
 * the Index health tab is where a human does.
 *
 * The Search tab is why the two live together. Its results carry Drive file and
 * folder names, and their trustworthiness depends entirely on how current the
 * index is — a floor plan renamed this morning is not findable by its new name
 * on an index that last synced yesterday. Splitting these across two routes
 * would let someone search a stale corpus without ever seeing that it was
 * stale, so the degraded state is surfaced on both tabs.
 *
 * The activity feed and tree browser are still deferred.
 *
 * Platform staff only, enforced by ancana_api (`IsPlatformStaff`); anyone else
 * gets a 403 and is told so rather than shown a blank page that would read as
 * "the index is empty".
 */
const DriveIndex = () => {
  const { health, isLoading, error, isForbidden, reload } = useIndexHealth();
  const [tab, setTab] = useState(HEALTH_TAB);

  if (isForbidden) {
    return (
      <Result
        status="403"
        title="Platform staff only"
        subTitle="Reading the Drive index status needs an active platform membership on the platform-owner operator."
      />
    );
  }

  const cachePercent = health?.cache_bytes && health?.cache_max_bytes
    ? Math.round((health.cache_bytes / health.cache_max_bytes) * 100)
    : 0;

  return (
    <Container>
      <PageHeader
        title="Drive Index"
        subTitle="Pathfinder — read-only index of propiedades + sales"
        // The timestamp and the reload button describe the health read, which
        // polls. The search does not poll, so on that tab they would claim a
        // freshness the results do not have.
        extra={tab === HEALTH_TAB ? [
          <span key="checked" className="note">
            {health?.checked_at
              ? `As of ${absolute(health.checked_at)} · refreshes every ${Math.round(REFRESH_MS / 1000)}s`
              : ''}
          </span>,
          <Button key="reload" icon={<ReloadOutlined />} onClick={reload} loading={isLoading} />,
        ] : undefined}
      />

      <div className="body">
        <Tabs activeKey={tab} onChange={setTab}>
          <Tabs.TabPane
            key={HEALTH_TAB}
            tab={(
              <span>
                Index health
                {/* A degraded index is the reason to distrust what the Search tab
                    shows, so it has to be visible from that tab, not only inside
                    this one. */}
                {health && health.status !== 'ok' && <Badge status="error" style={{ marginLeft: 8 }} />}
              </span>
            )}
          >
            <div className="tab-body">
            {isLoading && !health && <Skeleton active paragraph={{ rows: 4 }} />}

            {/* No report at all — a network failure, or ancana_api itself down. */}
            {error && !health && !isLoading && (
              <Alert type="error" showIcon message="Could not read the index status" description={error} />
            )}

            {health && <StatusBanner health={health} />}

            {health && health.status !== 'unavailable' && (
              <>
                <div className="counts">
                  <Statistic title="Nodes indexed" value={formatCount(health.nodes)} />
                  <Statistic title="Folders" value={formatCount(health.folders)} />
                  <Statistic title="Files" value={formatCount(health.files)} />
                  <Statistic
                    title="Reconcile findings"
                    value={formatCount(health.reconcile_findings)}
                    valueStyle={health.reconcile_findings ? { color: '#cf1322' } : undefined}
                  />
                  <Statistic title="Uptime" value={health.uptime || '—'} />
                </div>

                <div className="cache">
                  <Statistic
                    title="Blob cache"
                    value={`${formatBytes(health.cache_bytes)} / ${formatBytes(health.cache_max_bytes)}`}
                  />
                  <Progress percent={cachePercent} size="small" status="normal" />
                  <span className="note">{formatCount(health.cache_entries)} cached files</span>
                </div>

                <Table<IIndexRoot>
                  columns={columns}
                  dataSource={health.roots}
                  rowKey="root_key"
                  pagination={false}
                  loading={isLoading}
                  locale={{ emptyText: <Empty description="Pathfinder is tracking no roots" /> }}
                />

                <p className="note">
                  {formatCount(health.unparsed_nodes)} files are unparsed by the nomenclature
                  parser. That parser is still a stub, so this currently equals the file count
                  by design — it becomes a naming-drift signal only once the convention is
                  implemented, and is not an alarm today.
                </p>
              </>
            )}
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Search" key={SEARCH_TAB}>
            <div className="tab-body">
              <DriveSearch />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </Container>
  );
};

export default DriveIndex;
